/**
 * Where a buyer-campaign link lands (/welcome/:token).
 *
 * The Industry Graph already knows the shop, the person, the email and the
 * location, so the form arrives filled in and asks only for a password and
 * its confirmation. "Skip for now" goes straight to the platform without an
 * account. Both choices are recorded on the invitation so the CRM funnel
 * shows them.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { signInWithEmailAndPassword } from "firebase/auth";
import { firebaseAuth } from "../firebase";
import { sendRequest } from "../common/functions";
import { getTrackingIds } from "../visitorTracking";

type InvitationView = {
  token: string;
  company: string;
  name: string;
  email: string;
  phone: string;
  city: string;
  state: string;
  website: string;
  materials: { forms: string[]; materialFamilies: string[] };
  lots: { id: string; summary: string; priceText: string; location: string; materialFamily: string }[];
  offerAccountCreation: boolean;
  allowSkip: boolean;
  expired: boolean;
  signedUp: boolean;
  /** Campaign copy, already filled in ({name}, {company}, {materials}); empty strings mean the standard text. */
  landing?: { headline: string; paragraph: string; searchFor: string; materialFocus: string };
};

/** /api/users is the authenticated base; the public invitation routes sit beside it on /invite. */
const inviteBase = (): string => {
  const userApiBaseUrl = process.env.REACT_APP_USER_API_BASE_URL;
  if (!userApiBaseUrl) throw new Error("Missing REACT_APP_USER_API_BASE_URL");
  return userApiBaseUrl.replace(/\/users$/, "/invite");
};

const readError = async (response: Response, fallback: string): Promise<string> => {
  try {
    const body = await response.json();
    return typeof body?.error === "string" ? body.error : fallback;
  } catch {
    return fallback;
  }
};

const inputClass = "mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none";

export default function WelcomePage({ logoImg }: { logoImg: string }) {
  const { token = "" } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState<InvitationView | null>(null);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("This link is not valid.");
      setLoading(false);
      return;
    }
    // The visitor id (only when analytics cookies were accepted) ties later
    // product views back to the shop the email went to.
    const visitorId = getTrackingIds()?.visitorId;
    sendRequest(`${inviteBase()}/${encodeURIComponent(token)}${visitorId ? `?v=${encodeURIComponent(visitorId)}` : ""}`, { method: "GET" })
      .then(async (response) => {
        if (!response.ok) throw new Error(await readError(response, "This link is not valid."));
        const view = (await response.json()) as InvitationView;
        setInvite(view);
        setName(view.name || "");
        setCompany(view.company || "");
        setPhone(view.phone || "");
      })
      .catch((e) => setError(e instanceof Error ? e.message : "This link is not valid."))
      .finally(() => setLoading(false));
  }, [token]);

  const skip = async () => {
    setSaving(true);
    try {
      await sendRequest(`${inviteBase()}/${encodeURIComponent(token)}/skip`, { method: "POST", jsonBody: {} });
    } catch {
      // The visit itself is what matters; a failed note must not block them.
    }
    navigate(searchFor ? `/?q=${encodeURIComponent(searchFor)}` : "/");
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!invite) return;
    if (password.length < 8) {
      setError("Choose a password of at least eight characters.");
      return;
    }
    if (password !== confirmation) {
      setError("Those two passwords are not the same.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const response = await sendRequest(`${inviteBase()}/${encodeURIComponent(token)}/accept`, {
        method: "POST",
        jsonBody: { password, name, company, phone },
      });
      if (!response.ok) {
        setError(await readError(response, "The account could not be created."));
        return;
      }
      await signInWithEmailAndPassword(firebaseAuth, invite.email, password);
      navigate("/");
    } catch (e) {
      setError(e instanceof Error ? e.message : "The account could not be created.");
    } finally {
      setSaving(false);
    }
  };

  const materials = invite ? [...invite.materials.materialFamilies] : [];
  const forms = invite ? invite.materials.forms : [];
  const lots = invite?.lots || [];
  const searchFor = invite?.landing?.searchFor || (invite?.lots[0]?.materialFamily || materials[0] || "").replace("high-spec ", "").replace(" superalloys", " alloy");
  const headline = invite?.landing?.headline || "";
  const paragraph = invite?.landing?.paragraph || "";
  const where = invite ? [invite.city, invite.state].filter(Boolean).join(", ") : "";

  return (
    <div className="mx-auto md:w-[32em] px-4 py-10">
      <Link to="/">
        <img src={logoImg} alt="Kono" className="mx-auto mb-10 h-[4em] w-auto" />
      </Link>

      {loading && <p className="text-center text-sm text-gray-600">One moment…</p>}

      {!loading && !invite && (
        <div className="rounded border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {error || "This link is not valid."}{" "}
          You can still <Link to="/signup" className="underline">create an account</Link> or <Link to="/" className="underline">browse the stock</Link>.
        </div>
      )}

      {!loading && invite && (
        <>
          <h1 className="mb-2 text-center text-xl font-semibold">
            {headline || <>Welcome{invite.name ? `, ${invite.name.split(" ")[0]}` : ""}</>}
          </h1>
          {paragraph ? (
            <p className="mb-5 text-center text-sm text-gray-600">{paragraph}</p>
          ) : (
            <p className="mb-5 text-center text-sm text-gray-600">
              Kono lists high-performance alloys from US distributors with prices online.
              {invite.company && (
                <>
                  {" "}
                  We set this page up for <span className="font-medium text-gray-900">{invite.company}</span>
                  {where ? ` in ${where}` : ""}.
                </>
              )}
            </p>
          )}

          {lots.length > 0 ? (
            <div className="mb-5 rounded border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900">
              <div className="mb-1 font-medium">Picked for {invite.company || "your shop"}:</div>
              <ul className="space-y-1">
                {lots.map((l) => (
                  <li key={l.id}>
                    <Link to={`/product?id=${encodeURIComponent(l.id)}`} className="underline">
                      {l.summary}
                    </Link>
                    {l.priceText ? `, ${l.priceText}` : ""}
                    {l.location ? `, ${l.location}` : ""}
                  </li>
                ))}
              </ul>
              {searchFor && (
                <div className="mt-2">
                  <Link to={`/?q=${encodeURIComponent(searchFor)}`} className="underline">
                    See all {searchFor} in stock
                  </Link>
                </div>
              )}
            </div>
          ) : (
            materials.length > 0 && (
              <div className="mb-5 rounded border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900">
                Shops like yours buy {materials.join(", ")}
                {forms.length > 0 ? ` as ${forms.join(", ")}` : ""}.{" "}
                {searchFor && (
                  <Link to={`/?q=${encodeURIComponent(searchFor)}`} className="underline">
                    See {searchFor} in stock
                  </Link>
                )}
              </div>
            )
          )}

          {invite.signedUp && (
            <div className="rounded border border-green-300 bg-green-50 px-4 py-3 text-sm text-green-800">
              This invitation was already used to create an account. <Link to="/signin" className="underline">Sign in</Link> to continue.
            </div>
          )}

          {!invite.signedUp && invite.expired && (
            <div className="rounded border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              This link has expired. You can still <Link to="/signup" className="underline">create an account</Link> or <Link to="/" className="underline">browse the stock</Link>.
            </div>
          )}

          {!invite.signedUp && !invite.expired && invite.offerAccountCreation && (
            <form onSubmit={submit}>
              <p className="mb-3 text-sm text-gray-600">
                Your details are already filled in. Choose a password and you are in; check the rest and correct anything that is off.
              </p>
              <label className="block text-sm">
                <span className="text-gray-900">Email</span>
                <input type="email" className={`${inputClass} bg-gray-50`} value={invite.email} readOnly />
              </label>
              <label className="mt-3 block text-sm">
                <span className="text-gray-900">Your name</span>
                <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
              </label>
              <label className="mt-3 block text-sm">
                <span className="text-gray-900">Company</span>
                <input className={inputClass} value={company} onChange={(e) => setCompany(e.target.value)} autoComplete="organization" />
              </label>
              <label className="mt-3 block text-sm">
                <span className="text-gray-900">Phone</span>
                <input className={inputClass} value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="tel" />
              </label>
              <label className="mt-3 block text-sm">
                <span className="text-gray-900">Password</span>
                <input type="password" autoComplete="new-password" className={inputClass} value={password} onChange={(e) => setPassword(e.target.value)} />
              </label>
              <label className="mt-3 block text-sm">
                <span className="text-gray-900">Password again</span>
                <input type="password" autoComplete="new-password" className={inputClass} value={confirmation} onChange={(e) => setConfirmation(e.target.value)} />
              </label>

              {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

              <button type="submit" disabled={saving} className="mt-5 w-full rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
                {saving ? "Creating your account…" : "Create my account"}
              </button>
              {invite.allowSkip && (
                <button type="button" disabled={saving} onClick={skip} className="mt-2 w-full rounded border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                  Skip for now, just show me the stock
                </button>
              )}
              <p className="mt-4 text-center text-xs text-gray-500">
                Already have an account? <Link to="/signin" className="underline">Sign in</Link>
              </p>
            </form>
          )}

          {!invite.signedUp && !invite.expired && !invite.offerAccountCreation && (
            <div className="text-center">
              <button type="button" disabled={saving} onClick={skip} className="w-full rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
                Show me the stock
              </button>
              <p className="mt-4 text-xs text-gray-500">
                Want an account? <Link to="/signup" className="underline">Sign up</Link>
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
